import React, { useState, useMemo } from 'react';
import { Search, Plus } from 'lucide-react';
import { NgapAct } from '../../../../types/ngap';
import { useAppContext } from '../../../../store';
import { validateSearchInput } from '../../../../utils/inputValidation';
import { generateNgapCodeLabel } from '../../../../utils/urlUtils';

interface ActSelectorProps {
  onAddToCart: (act: NgapAct, quantity: number) => void;
  selectedActId: string | null;
  onSelectAct: (actId: string | null) => void;
  darkMode: boolean;
}

const ActSelector: React.FC<ActSelectorProps> = ({ onAddToCart, selectedActId, onSelectAct, darkMode }) => {
  const { state } = useAppContext();
  const { acts } = state.data;
  const [searchQuery, setSearchQuery] = useState('');
  const quantity = 1;

  const filteredActs = useMemo(() => {
    if (!searchQuery.trim()) return acts.slice(0, 10);
    const query = searchQuery.toLowerCase().trim();
    return acts.filter(act => {
      const searchableText = [act.libelle, act.lettre_cle, act.cotation_valeur, act.theme, act.reeducation].filter(Boolean).join(' ').toLowerCase();
      return searchableText.includes(query);
    }).slice(0, 20);
  }, [acts, searchQuery]);

  const handleSearchChange = (value: string) => {
    const validation = validateSearchInput(value);
    setSearchQuery(validation.sanitizedValue);
  };

  const handleAddToCart = (act: NgapAct) => {
    onAddToCart(act, quantity);
    onSelectAct(null);
  };

  const selectedAct = selectedActId ? acts.find(act => String(act.ID) === selectedActId) : null;

  return (
    <div className={`rounded-lg border p-6 ${darkMode ? 'bg-appines-petrol border-appines-white/12' : 'bg-appines-white border-[rgba(1,42,54,0.12)]'}`}>
      <div className="mb-4">
        <div className={`relative rounded-lg border ${darkMode ? 'border-appines-white/20 bg-appines-petrol' : 'border-[rgba(1,42,54,0.2)] bg-appines-white'}`}>
          <div className="flex items-center">
            <Search className={`h-5 w-5 ml-3 ${darkMode ? 'text-appines-white/45' : 'text-appines-petrol/45'}`} />
            <input
              type="text" value={searchQuery}
              onChange={(e) => handleSearchChange(e.target.value)}
              placeholder="Rechercher un acte NGAP..."
              className={`w-full px-3 py-3 bg-transparent border-none outline-none ${darkMode ? 'text-appines-white placeholder:text-appines-white/45' : 'text-appines-black placeholder:text-appines-petrol/45'}`}
            />
          </div>
        </div>
      </div>

      {searchQuery && (
        <div className="mb-4">
          <h4 className={`text-sm font-medium mb-2 ${darkMode ? 'text-appines-white/65' : 'text-appines-petrol/65'}`}>
            Résultats de recherche ({filteredActs.length})
          </h4>
          <div className="max-h-60 overflow-y-auto space-y-2">
            {filteredActs.map(act => (
              <div
                key={act.ID}
                className={`p-3 rounded-lg border cursor-pointer transition-colors ${
                  selectedActId === String(act.ID)
                    ? 'border-appines-emerald bg-appines-glass/20'
                    : darkMode
                      ? 'border-appines-white/12 bg-appines-petrol hover:bg-appines-white/5'
                      : 'border-[rgba(1,42,54,0.12)] bg-[rgba(1,42,54,0.04)] hover:bg-[rgba(1,42,54,0.08)]'
                }`}
                onClick={() => onSelectAct(String(act.ID))}
              >
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <div className="flex items-center mb-1">
                      <span className={`text-sm font-medium px-2 py-1 rounded ${darkMode ? 'bg-appines-emerald/15 text-appines-emerald' : 'bg-appines-glass/25 text-appines-petrol'}`}>
                        {generateNgapCodeLabel(act, 'auto')}
                      </span>
                    </div>
                    <p className={`text-sm ${darkMode ? 'text-appines-white/65' : 'text-appines-petrol/65'}`}>{act.libelle}</p>
                  </div>
                  <button
                    onClick={(e) => { e.stopPropagation(); handleAddToCart(act); }}
                    className="ml-2 p-2 bg-appines-emerald text-appines-petrol rounded-lg hover:bg-[#22b886] transition-colors"
                    title="Ajouter au simulateur"
                  >
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {selectedAct && (
        <div className={`p-4 rounded-lg border-2 border-appines-emerald ${darkMode ? 'bg-appines-emerald/10' : 'bg-appines-glass/20'}`}>
          <div className="flex items-center justify-between mb-3">
            <h4 className={`font-medium ${darkMode ? 'text-appines-emerald' : 'text-appines-petrol'}`}>Acte sélectionné</h4>
            <button
              onClick={() => handleAddToCart(selectedAct)}
              className="px-4 py-2 bg-appines-emerald text-appines-petrol rounded-lg hover:bg-[#22b886] transition-colors text-sm font-medium"
            >
              Ajouter ({quantity}/jour)
            </button>
          </div>
          <div className="space-y-2">
            <p className={`font-medium ${darkMode ? 'text-appines-white' : 'text-appines-black'}`}>
              {generateNgapCodeLabel(selectedAct, 'auto')} - {selectedAct.libelle}
            </p>
            <p className={`text-sm ${darkMode ? 'text-appines-white/45' : 'text-appines-petrol/65'}`}>
              {selectedAct.theme} - {selectedAct.reeducation}
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

export default ActSelector;
